import React, { useEffect, useRef, useState } from 'react';
import { CalendarSolid } from '@/components/Icons/CalendarSolid';
import { ChevronDown } from '@/components/Icons/ChevronDown';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { formatPeriodRange } from '@/pages/Overview/period';
import { PeriodRangePopover } from '@/pages/Overview/PeriodRangePopover';
import { PeriodSelectSheet } from '@/pages/Overview/PeriodSelectSheet';
import { StudyYearPeriodType, type StudyYearPeriod } from '@/types/overview';
import { tv } from '@/utils/tv';

interface Props {
  periods: StudyYearPeriod[];
  value: StudyYearPeriod;
  isAlignedRight?: boolean;
  onChange: (period: StudyYearPeriod) => void;
}

const triggerChevron = tv({
  base: 'size-4 text-primary transition-transform duration-200',
  variants: {
    isOpen: {
      true: 'rotate-180',
      false: '',
    },
  },
});

export const PeriodPicker: React.FC<Props> = ({
  periods,
  value,
  isAlignedRight = false,
  onChange,
}) => {
  const isMobile = useIsMobile();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const label =
    value.type === StudyYearPeriodType.Custom
      ? formatPeriodRange(value)
      : value.name;

  const closeDropdown = () => {
    setIsCalendarOpen(false);
    setIsOpen(false);
  };

  const handleSelect = (period: StudyYearPeriod) => {
    closeDropdown();
    onChange(period);
  };

  useCloseOnBreakpointChange(isOpen, closeDropdown);

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    const closeTopLayer = () => {
      setIsCalendarOpen(false);

      if (!isCalendarOpen) {
        setIsOpen(false);
      }
    };

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      closeTopLayer();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTopLayer();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile, isCalendarOpen]);

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="ml-auto flex shrink-0 cursor-pointer items-center gap-2 rounded-2xl bg-purple-soft px-2 py-1 text-primary"
        >
          <CalendarSolid className="size-3.5 shrink-0" />
          <span className="text-caption-12 font-medium whitespace-nowrap">
            {label}
          </span>
        </button>
        {isOpen && (
          <PeriodSelectSheet
            periods={periods}
            value={value}
            onClose={() => setIsOpen(false)}
            onApply={handleSelect}
          />
        )}
      </>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${isOpen ? 'z-50' : ''}`}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`flex h-8 cursor-pointer items-center gap-1 rounded-3xl px-2 py-0.5 shadow-card transition-colors ${isOpen ? 'bg-background' : 'bg-popover hover:bg-info-surface'}`}
      >
        <span className="px-1 text-caption-12 text-primary">{label}</span>
        <ChevronDown className={triggerChevron({ isOpen })} />
      </button>

      {isOpen && (
        <PeriodRangePopover
          periods={periods}
          value={value}
          isAlignedRight={isAlignedRight}
          isCalendarOpen={isCalendarOpen}
          onCalendarOpenChange={setIsCalendarOpen}
          onApply={handleSelect}
        />
      )}
    </div>
  );
};
